import React, { useEffect, useRef } from 'react';
import {
  CustomCellEditorProps,
  CustomCellRendererProps,
  CustomHeaderProps,
  CustomTooltipProps
} from '@ag-grid-community/react';
import clsx from 'clsx';
import { formatCurrency } from '../../components/tables/table-utils';
import {
  getPlatformEntityType,
  getPlatformName
} from 'shared/src/domain/media-platform/media-platforms';
import { equalRoundedToInt, equalToTwoDecimalPlaces } from 'shared/src/utils/number/number-utils';
import { PlaceholderTextField } from '../../components/tables/cell-renderers/placeholder-text-field';
import { CalculatedValue } from '../../components/tables/cell-renderers/calculated-value';
import { CenterTrimmedValue } from '../../components/tables/cell-renderers/center-trimmed-value';
import { PacingIndicator } from '../../components/tables/cell-renderers/pacing-indicator';
import { ViewButton } from '../../components/tables/cell-renderers/view-button';
import { BarChart } from '../../icons/bar-chart';
import { TooltipContainer } from '../../components/tables/cell-renderers/utils';
import { MediaPlanTableData } from '@/features/media-plans/media-plans-table';
import { UnitPriceType } from 'shared/src/domain/line-item/line-item-channels';
import { useChannelRatesContext } from '@/features/strategy/channel-rates-context';

export type CellRendererProps<T> = CustomCellRendererProps<MediaPlanTableData, T>;
type HeaderRendererProps<T> = CustomHeaderProps<MediaPlanTableData, T>;
type TooltipRendererProps<T> = CustomTooltipProps<MediaPlanTableData, T>;
type CellEditorProps<T> = CustomCellEditorProps<MediaPlanTableData, T>;

export function HeaderComponent({ displayName }: HeaderRendererProps<string>) {
  return <div className="capitalize">{displayName}</div>;
}

export function ViewButtonRenderer({
  colorMap,
  data
}: CellRendererProps<string> & { colorMap: Record<string, string> }) {
  if (!data || data.isTotal || !data.media_buy?.id) return null;

  const color = colorMap[data.media_buy.id];

  return (
    <div className="flex h-full items-center">
      <div className="mr-1 h-[24px] w-[6px] rounded-full" style={{ background: color }} />
      <ViewButton path={`mediaplans/${data.id}`} />
    </div>
  );
}

export function NameRenderer({ data, value }: CellRendererProps<string>) {
  if (data?.isTotal) return null;
  return value || <PlaceholderTextField placeholder="Name this media plan" />;
}

export function PlatformEntityRenderer({
  data,
  value
}: CustomCellRendererProps<MediaPlanTableData>) {
  const { platforms } = useChannelRatesContext();
  return data?.isTotal ? (
    <TotalsRowCell value={'1'} unit="Platform(s)" />
  ) : (
    `${getPlatformName(platforms, value)} ${getPlatformEntityType(value)}`
  );
}

export function DeliveryPacingRenderer({ value }: CellRendererProps<number>) {
  return <PacingIndicator value={value} icon={<BarChart />} />;
}

export function SpendPacingRenderer({ value }: CellRendererProps<number>) {
  return <PacingIndicator value={value} icon={<BarChart />} />;
}

export function MarginRenderer({ data, value }: CellRendererProps<number>) {
  return (
    <PacingIndicator value={value} colorBase={data?.metrics?.target_margin} icon={<BarChart />} />
  );
}

export function BudgetRenderer({
  data,
  valueFormatted,
  lineItemBudget,
  value
}: CellRendererProps<number> & { lineItemBudget: number }) {
  return data?.isTotal ? (
    <TotalsRowCell
      value={valueFormatted}
      isAlarm={!equalToTwoDecimalPlaces(value || 0, lineItemBudget)}
    />
  ) : (
    valueFormatted
  );
}

type TargetUnitCostRendererProps = CellRendererProps<number> & {
  lineItemTarget: number;
  unitPriceType: UnitPriceType;
};

export function TargetUnitCostRenderer(props: TargetUnitCostRendererProps) {
  const { data, value, unitPriceType, lineItemTarget } = props;
  if (value == null) return '--';
  const adjustedValue = unitPriceType.name === 'CPM' ? value * 1000 : value;
  const adjustedTargetValue = unitPriceType.name === 'CPM' ? lineItemTarget * 1000 : lineItemTarget;
  const valueFormatted = formatCurrency(adjustedValue);
  return data?.isTotal ? (
    <TotalsRowCell
      value={valueFormatted}
      isAlarm={!equalToTwoDecimalPlaces(adjustedValue, adjustedTargetValue)}
    />
  ) : (
    valueFormatted
  );
}

type PlannedMarginRendererProps = CellRendererProps<number> & {
  lineItemTargetMargin: number | null | undefined;
};

export function PlannedMarginRenderer(props: PlannedMarginRendererProps) {
  const { data, value, valueFormatted, lineItemTargetMargin } = props;

  return data?.isTotal ? (
    <TotalsRowCell
      value={valueFormatted}
      isAlarm={!equalToTwoDecimalPlaces(value, lineItemTargetMargin)}
    />
  ) : (
    <CalculatedValue disabled={false} value={valueFormatted} />
  );
}

type EstimatedUnitsRendererProps = CellRendererProps<number> & {
  lineItemTargetUnits?: number;
};

export function EstimatedUnitsRenderer(params: EstimatedUnitsRendererProps) {
  const { data, valueFormatted, value, lineItemTargetUnits } = params;
  return data?.isTotal ? (
    <TotalsRowCell
      value={valueFormatted}
      isAlarm={!equalRoundedToInt(value, lineItemTargetUnits)}
    />
  ) : (
    <CalculatedValue disabled={false} value={valueFormatted} />
  );
}

export function DeliveredUnitsRenderer({ data, valueFormatted }: CellRendererProps<string>) {
  return data?.isTotal ? <TotalsRowCell value={valueFormatted} /> : valueFormatted;
}

export function MediaSpendRenderer({ data, value }: CellRendererProps<number>) {
  return data?.isTotal ? (
    <TotalsRowCell value={formatCurrency(value || 0)} />
  ) : (
    formatCurrency(value || 0)
  );
}

export function PlatformBuyNameRenderer({ data, value }: CellRendererProps<string>) {
  if (!data || data.isTotal || !value) return null;
  return <CenterTrimmedValue value={value} />;
}

export function PlatformBuyTooltipRenderer({ value }: TooltipRendererProps<string>) {
  return <TooltipContainer>{value}</TooltipContainer>;
}

type TotalsRowCellProps = {
  value: string | null | undefined;
  unit?: string;
  isAlarm?: boolean;
};

export function TotalsRowCell({ value, unit, isAlarm }: TotalsRowCellProps) {
  return (
    <div className={clsx(isAlarm && 'text-red-500')}>
      <span className="font-bold">{value || '--'}</span>
      {unit && <span>{` ${unit}`}</span>}
    </div>
  );
}

type UnitValueEditorProps = CellEditorProps<number> & {
  unitPriceType: UnitPriceType;
};

export function UnitValueEditor({ value, onValueChange, unitPriceType }: UnitValueEditorProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  function handleChange(value: string) {
    const num = parseFloat(value);
    if (isNaN(num)) {
      onValueChange(undefined);
    } else {
      onValueChange(unitPriceType.name === 'CPM' ? num / 1000 : num);
    }
  }

  const displayValue = unitValue(unitPriceType, value);

  return (
    <div className="relative flex h-full w-full items-center rounded-[2px] border border-[#2096f3] bg-white">
      <div className="absolute left-3">$</div>
      <input
        className="outline-hidden w-full rounded-none border-0 border-none pl-6 text-sm focus:ring-0"
        ref={inputRef}
        type="number"
        value={displayValue === 0 ? displayValue : displayValue || ''}
        onChange={({ target }) => handleChange(target.value)}
      />
    </div>
  );
}

function unitValue(unitPriceType: UnitPriceType, value: number | null | undefined) {
  if (value == null) return null;
  return unitPriceType.name === 'CPM' ? value * 1000 : value;
}
